import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  memo,
} from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const POLLING_INTERVAL = 15000;

/* --------------------------------------------------------------
   RoleBadge – memoised, clean gradient/shadow map
   -------------------------------------------------------------- */
const RoleBadge = memo(function RoleBadge({ roleName }) {
  const n = (roleName || "").toLowerCase();

  const gradients = {
    admin: "linear-gradient(135deg, #DC2626, #EF4444)",
    manager: "linear-gradient(135deg, #0EA5E9, #38BDF8)",
    agent: "linear-gradient(135deg, #16A34A, #22C55E)",
    user: "linear-gradient(135deg, #6B7280, #9CA3AF)",
  };
  const shadows = {
    admin: "0 0 10px rgba(239,68,68,0.5)",
    manager: "0 0 10px rgba(14,165,233,0.5)",
    agent: "0 0 10px rgba(34,197,94,0.5)",
    user: "0 0 10px rgba(156,163,175,0.5)",
  };

  const style = {
    padding: "6px 12px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.8rem",
    display: "inline-block",
    textTransform: "capitalize",
    color: n === "user" ? "#1E293B" : "#fff",
    background: gradients[n] || gradients.user,
    boxShadow: shadows[n] || shadows.user,
  };

  return <span style={style}>{roleName || "—"}</span>;
});

/* --------------------------------------------------------------
   Main Component
   -------------------------------------------------------------- */
const RoleAssignmentPage = () => {
  /* ---------- state ---------- */
  const [roles, setRoles] = useState([]);               // [{id, name}]
  const [users, setUsers] = useState([]);               // [{id, name, email, role}]
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [selUserId, setSelUserId] = useState("");
  const [selRoleId, setSelRoleId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const API = (process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api")
    .replace(/\/+$/, "");

  const mounted = useRef(true);
  const fetching = useRef(false);

  /* ---------- fetch roles ---------- */
  const fetchRoles = useCallback(async () => {
    try {
      const r = await fetch(`${API}/roles/fetch_roles.php`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.message ?? "Roles load failed");
      setRoles(j.data ?? []);
    } catch (e) {
      toast.error(e.message ?? "Roles error");
    }
  }, [API]);

  /* ---------- fetch users (with role) ---------- */
  const fetchUsers = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin_panel/v1/users/users.php`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error ?? "Users load failed");
      setUsers(j.data ?? []);
    } catch (e) {
      toast.error(e.message ?? "Users error");
    }
  }, [API]);

  /* ---------- create role ---------- */
  const createRole = async () => {
    if (!newRoleName.trim() || creating) return;
    setCreating(true);
    try {
      const r = await fetch(`${API}/roles/create_role.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newRoleName.trim(),
          description: newRoleDesc.trim(),
        }),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message ?? "Create failed");
      toast.success(j.message ?? "Role created");
      setNewRoleName("");
      setNewRoleDesc("");
      await fetchRoles();
    } catch (e) {
      toast.error(e.message ?? "Create error");
    } finally {
      setCreating(false);
    }
  };

  /* ---------- assign role ---------- */
  const assignRoleToUser = async () => {
  if (!selUserId || !selRoleId || assigning) return;

  // Find user and role using numeric comparison
  const user = users.find(u => u.id === selUserId);
  const role = roles.find(r => r.id === selRoleId);

  if (!role) {
    toast.error("Invalid role selected");
    return;
  }

  if (user?.role === role.name) {
    toast.warn("User already has this role");
    return;
  }

  setAssigning(true);
  try {
    const res = await fetch(`${API}/roles/assign_role.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: selUserId,
        role_id: selRoleId,
      }),
    });

    const json = await res.json();
    if (!json.success) throw new Error(json.message || "Failed");
    toast.success(json.message || "Role assigned");
    setSelUserId("");
    setSelRoleId("");
    await fetchUsers();
  } catch (err) {
    toast.error(err.message || "Network error");
  } finally {
    setAssigning(false);
  }
};



  /* ---------- initial load ---------- */
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([fetchRoles(), fetchUsers()]);
      setLoading(false);
    };
    go();
    return () => {
      mounted.current = false;
    };
  }, [fetchRoles, fetchUsers]);

  /* ---------- polling (no race) ---------- */
  useEffect(() => {
    const id = setInterval(async () => {
      if (fetching.current || loading) return;
      fetching.current = true;
      try {
        await Promise.all([fetchRoles(), fetchUsers()]);
      } finally {
        if (mounted.current) fetching.current = false;
      }
    }, POLLING_INTERVAL);
    return () => clearInterval(id);
  }, [fetchRoles, fetchUsers, loading]);

  /* ---------- render ---------- */
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto",
      }}
    >
      <ToastContainer theme="dark" />

      <div
        style={{
          background: "rgba(255,255,255,0.08)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "1000px",
          margin: "0 auto",
          boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
          backdropFilter: "blur(8px)",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "25px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: 600 }}>Role Management</h1>
          <p style={{ color: "#E0E7FF" }}>
            Create roles & assign them to users.
          </p>
        </div>

        {/* Global error */}
        {error && (
          <div
            style={{
              background: "rgba(255,255,255,0.1)",
              border: "1px solid #F87171",
              borderRadius: "10px",
              padding: "15px",
              color: "#FCA5A5",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* ---------- CREATE ROLE ---------- */}
        <div
          style={{
            background: "rgba(255,255,255,0.1)",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "25px",
          }}
        >
          <h3 style={{ margin: "0 0 15px 0", color: "#E0E7FF" }}>
            Create New Role
          </h3>
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              placeholder="Role Name"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              style={{
                flex: 1,
                minWidth: "180px",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontSize: "0.95rem",
              }}
            />
            <input
              placeholder="Description (optional)"
              value={newRoleDesc}
              onChange={(e) => setNewRoleDesc(e.target.value)}
              style={{
                flex: 1,
                minWidth: "200px",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontSize: "0.95rem",
              }}
            />
            <button
              onClick={createRole}
              disabled={creating}
              style={{
                background: "#16A34A",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                fontWeight: 600,
                cursor: "pointer",
                minWidth: "120px",
                opacity: creating ? 0.7 : 1,
              }}
            >
              {creating ? "Creating…" : "Create Role"}
            </button>
          </div>
        </div>

        {/* ---------- ASSIGN ROLE ---------- */}
        <div
          style={{
            background: "rgba(255,255,255,0.1)",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "30px",
          }}
        >
          <h3 style={{ margin: "0 0 15px 0", color: "#E0E7FF" }}>
            Assign Role to User
          </h3>
          <div
            style={{
              display: "flex",
              gap: "15px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div style={{ flex: 1, minWidth: "220px" }}>
              <label
                htmlFor="userSel"
                style={{
                  display: "block",
                  marginBottom: "6px",
                  color: "#E0E7FF",
                  fontWeight: 500,
                }}
              >
                Select User
              </label>
              <select
                id="userSel"
                value={selUserId || 0} // default to 0 if not selected
                onChange={(e) => setSelUserId(Number(e.target.value))} // convert to number
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "none",
                  background: "rgba(255,255,255,0.9)",
                  color: "#1E293B",
                  fontSize: "0.95rem",
                }}
              >
                <option value={0}>-- Select User --</option> {/* numeric default */}
                {users.map((u) => (
                  <option key={u.id} value={u.id}> {/* numeric value */}
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: 1, minWidth: "180px" }}>
  <label
  htmlFor="roleSel"
  style={{
    display: "block",
    marginBottom: "6px",
    color: "#E0E7FF",
    fontWeight: 500,
  }}
>
  Select Role
</label>
<select
  id="roleSel"
  value={selRoleId || 0} // default to 0 if not selected
  onChange={(e) => setSelRoleId(Number(e.target.value))} // convert to number
  style={{
    width: "100%",
    padding: "10px 14px",
    borderRadius: "8px",
    border: "none",
    background: "rgba(255,255,255,0.9)",
    color: "#1E293B",
    fontSize: "0.95rem",
  }}
>
  <option value={0}>-- Select Role --</option> {/* numeric default */}
  {roles.map((r) => (
    <option key={r.id} value={r.id}> {/* numeric value */}
      {r.name}
    </option>
  ))}
</select>

</div>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button
              onClick={async () => {
                // Convert IDs to numbers before sending
                const userIdNum = Number(selUserId);
                const roleIdNum = Number(selRoleId);

                if (!userIdNum || !roleIdNum) {
                  toast.error("Please select both a user and a role.");
                  return;
                }

                setAssigning(true);
                try {
                  const res = await fetch(`${API}/roles/assign_role.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      user_id: userIdNum,
                      role_id: roleIdNum,
                    }),
                  });

                  const json = await res.json();
                  if (!json.success) throw new Error(json.message || "Failed");
                  toast.success(json.message || "Role assigned successfully.");
                  setSelUserId("");
                  setSelRoleId("");
                  await fetchUsers();
                } catch (err) {
                  toast.error(err.message || "Network error");
                } finally {
                  setAssigning(false);
                }
              }}
              disabled={assigning}
              style={{
                background: "#3B82F6",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                fontWeight: 600,
                cursor: "pointer",
                minWidth: "130px",
                height: "42px",
                opacity: assigning ? 0.7 : 1,
              }}
            >
              {assigning ? "Assigning…" : "Assign Role"}
            </button>
          </div>
          </div>
        </div>

        {/* ---------- USERS TABLE (uses user.role) ---------- */}
        <div
          style={{
            overflowX: "auto",
            background: "rgba(255,255,255,0.9)",
            borderRadius: "10px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              color: "#1E293B",
            }}
          >
            <thead>
              <tr style={{ background: "#1E3A8A", color: "#fff" }}>
                <th style={{ padding: "14px", textAlign: "left" }}>User</th>
                <th style={{ padding: "14px", textAlign: "left" }}>Email</th>
                <th style={{ padding: "14px", textAlign: "left" }}>
                  Current Role
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                /* skeleton */
                [...Array(4)].map((_, i) => (
                  <tr
                    key={`sk-${i}`}
                    style={{
                      background: i % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                    }}
                  >
                    {[...Array(3)].map((__, j) => (
                      <td key={j} style={{ padding: "12px" }}>
                        <div
                          style={{
                            height: "14px",
                            background: "#CBD5E1",
                            borderRadius: "4px",
                            width: "70%",
                            margin: "auto",
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#475569",
                    }}
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((u, i) => (
                  <tr
                    key={u.id}
                    style={{
                      background: i % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <td style={{ padding: "12px", fontWeight: 500 }}>
                      {u.name}
                    </td>
                    <td style={{ padding: "12px" }}>{u.email}</td>
                    <td style={{ padding: "12px" }}>
                      {u.role ? <RoleBadge roleName={u.role} /> : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignmentPage;